import './VariantStats.scoped.scss'
import BackdropModal from 'Shared/components/BackdropModal/BackdropModal';
import ServerWaitButton from 'Shared/components/ServerWaitButton';
import SmsPreview from 'CampaignBuilder/components/Steps/SmsPreview';
import EmailPreview from 'Shared/components/EmailPreview'
import useEmailPreview from 'Shared/hooks/useEmailPreview'
import { formatFinancial } from './utils/dateAndMathsUtils'

const VariantStats = ({ sendPhase, variants, campaignType }) => {
  const [widerVariantId, setWiderVariantId] = useState(null)
  const contentVariants = useSel(s => {
    if (campaignType === 'email') {
      return s.campaign?.emailBlast?.contentVariants || []
    } else {
      return s.campaign?.textMessageBlast?.contentVariants || []
    }
  })
  const [showConfirmation, setShowConfirmation] = useState(false)
  const currentSendPhase = useSel(s => s.campaign?.currentSendPhase)
  const error = useSel((s) => s.local.error)
  const { emailBlast = {}, search = {} } = useSel(s => s.campaign)
  const { campaignId } = useSel(s => s.local)
  const { name: searchName } = search
  const subjectLines = useSel(s => s.campaign?.emailBlast?.subjectLines || [])
  const mergeTagTests = useSel(s => s.campaign?.emailBlast?.mergeTagTests || [])
  const {html: previewHtml, subjectLine: previewSubjectLine} = emailBlast
    ? useEmailPreview([], campaignId, emailBlast.id, widerVariantId)
    : {html: null, subjectLine: null}
  const orderedVariants = _.orderBy(variants, 'id')
  const act = useAct()
  const maxClickRate = _.maxBy(variants, variant => parseFloat(variant.clickRate))?.clickRate || 0
  const maxReplyRate = _.maxBy(variants, variant => parseFloat(variant?.replyRate || 0))?.replyRate || 0
  const maxOpenRate = _.maxBy(variants, variant => parseFloat(variant?.openRate || 0))?.openRate || 0
  const maxDonationAmount = _.maxBy(variants, variant => variant?.totalDonationsGbp || 0)?.totalDonationsGbp || 0
  const hasDonations = _.some(variants, variant => (variant?.donationCount || 0) > 0)

  function closeModal() {
    setWiderVariantId(null)
    setShowConfirmation(false)
  }

  function confirmSendWider() {
    if (campaignType === 'email') {
      var promise = act.campaignBuilder.sendEmailBlastWider(widerVariantId)
    } else if (_.startsWith(campaignType, 'sms_')) {
      var promise = act.campaignBuilder.sendTextMessageBlastWider(widerVariantId)
    }

    return promise.then(() => {
      setShowConfirmation(true)
    })
      .catch(() => {
        act.local.upsert({ error: 'An error occurred while sending the wider version' })
        closeModal()
      })
  }

  function redirect() {
    window.location.href = '/admin/campaigns'
  }

  function getVariantContent(variantId) {
    const contentVariant = contentVariants.find(cv => cv.id === variantId)
    if (!contentVariant) return null

    if (campaignType === 'email') {
      // Handle both formats: direct subjectLine in content OR subjectLineId reference
      let subjectLine = contentVariant.content?.subjectLine
      if (!subjectLine && contentVariant.content?.subjectLineId) {
        subjectLine = subjectLines.find(sl => sl.id === contentVariant.content.subjectLineId)?.subjectLine
      }

      const mergeTagContent = mergeTagTests.map(mt => {
        const content = mt.mergeTagTestVersions?.find(v => v.id === contentVariant.content?.mergeTags?.[mt.id])?.content || ''
        return { name: mt.name, content }
      }).filter(mt => mt.content)

      return { subjectLine, mergeTagContent }
    } else {
      return { body: contentVariant.content?.body }
    }
  }

  const isFinal = variants.length === 1
  const showSendWider = !isFinal
  const maxConversionRate = _.maxBy(variants, variant => parseFloat(variant?.conversionRate || 0))?.conversionRate || 0

  return (
    <div className="variant-list">
      {error && <div className="error">{error}</div>}

      {/* Variant cards */}
      {orderedVariants.map((variant, index) => {
        const content = getVariantContent(variant.id)
        const letter = String.fromCharCode(65 + index)

        return (
          <div key={variant.id} className="variant-card">
            {/* Card header */}
            <div className="variant-card-header">
              {!isFinal && <div className="variant-letter">{letter}</div>}
              <div className="variant-card-content">
                {campaignType === 'email' && content?.subjectLine && (
                  <span className="variant-info"><strong>Subject:</strong> {content.subjectLine}</span>
                )}
                {campaignType === 'email' && !content?.subjectLine && content?.mergeTagContent?.length > 0 && (
                  <div className="variant-merge-tags">
                    {content.mergeTagContent.map((mt, i) => (
                      <span key={i} className="variant-info"><strong>{mt.name}:</strong> {mt.content}</span>
                    ))}
                  </div>
                )}
                {campaignType !== 'email' && content?.body && (
                  <span className="variant-info"><strong>Content:</strong> {content.body}</span>
                )}
              </div>
              <div className="variant-card-actions">
                {variant.sentWider && <span className="sent-wider-badge">Sent wider</span>}
                {showSendWider && String(currentSendPhase) === String(sendPhase) && (
                  <button className='button primary small' onClick={() => setWiderVariantId(variant.id)}>
                    Send wider
                  </button>
                )}
              </div>
            </div>

            {/* Card stats */}
            <div className="variant-card-stats">
              <div className="stat-item">
                <span className="stat-header">{campaignType === 'email' ? 'EMAILS' : 'TEXTS'}</span>
                <span className="stat-value">{variant.entityCount.toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-header">{campaignType === 'email' ? 'OPENS' : 'CLICKS'}</span>
                {campaignType === 'email' ? (
                  <span className={`stat-value ${variant.openRate > 0 && variant.openRate === maxOpenRate ? 'stat-highlight' : ''}`}>{variant.openRate}%</span>
                ) : (
                  <span className={`stat-value ${variant.clickRate > 0 && variant.clickRate === maxClickRate ? 'stat-highlight' : ''}`}>{variant.clickRate}%</span>
                )}
              </div>
              <div className="stat-item">
                <span className="stat-header">{campaignType === 'email' ? 'CLICKS' : 'REPLIES'}</span>
                {campaignType === 'email' ? (
                  <span className={`stat-value ${variant.clickRate > 0 && variant.clickRate === maxClickRate ? 'stat-highlight' : ''}`}>{variant.clickRate}%</span>
                ) : (
                  <span className={`stat-value ${variant.replyRate > 0 && variant.replyRate === maxReplyRate ? 'stat-highlight' : ''}`}>{variant.replyRate}%</span>
                )}
              </div>
              {campaignType === 'email' && (
                <div className="stat-item">
                  <span className="stat-header">CONVERSION</span>
                  <span className={`stat-value ${variant.conversionRate > 0 && variant.conversionRate === maxConversionRate ? 'stat-highlight' : ''}`}>{variant.conversionRate}%</span>
                </div>
              )}
              <div className="stat-item">
                <span className="stat-header">UNSUBS</span>
                <span className="stat-value">{variant.unsubscribeCount.toLocaleString()}</span>
              </div>
              {hasDonations && (
                <div className="stat-item">
                  <span className="stat-header">DONATIONS</span>
                  <span className={`stat-value ${variant.totalDonationsGbp > 0 && variant.totalDonationsGbp === maxDonationAmount ? 'stat-highlight' : ''}`}>
                    {(variant.donationCount || 0) > 0
                      ? formatFinancial(variant.totalDonationsGbp || 0)
                      : '-'
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
        )
      })}
      <BackdropModal open={!!widerVariantId} title={"Send wider"} onCancel={closeModal} maxWidth='md'>
        {!showConfirmation && (
          <div className='row'>
            <div className='column half'>
              <p>Send this version to the rest of the audience</p>
              <ServerWaitButton
                onClick={confirmSendWider}
                className="button primary margin-right"
              >
                Send
              </ServerWaitButton>
            </div>
            {_.startsWith(campaignType, 'sms_') && (
              <div className='column half'>
                <SmsPreview content={contentVariants.find(cv => cv.id === widerVariantId)?.content || {}} short />
              </div>
            )}
            {_.startsWith(campaignType, 'email') && (
              <div className='column half overflow-y'>
                <EmailPreview
                  html={previewHtml}
                  subjectLine={previewSubjectLine}
                  senderName={emailBlast?.emailSender?.name}
                  searchName={searchName}
                  iframeStyle={{ transform: 'scale(0.7)', width: '100%' }}
                  fromPreview={true}
                  inline={true}
                />
              </div>
            )}
          </div>
        )}
        {showConfirmation && (
          <div>
            <h3>This version will be sent to the rest of your campaign's audience shortly.</h3>
            <button className="button primary margin-right" onClick={closeModal}>Close window</button>
            <button className="button secondary" onClick={redirect}>Back to campaigns list</button>
          </div>
        )}
      </BackdropModal>
    </div>
  );
};

export default VariantStats;
